/**
 * Sentry PHI scrubbing for the legacy webpack bundle.
 *
 * This is the JS counterpart to frontend/src/shared/sentryScrub.ts (the Vite
 * apps) — the two can't share a module across the webpack/Vite boundary, so the
 * legacy bundle carries its own copy. Its job is different from the Vite one:
 * the cloud/legacy surface has no patient access tokens in URLs, but the exam,
 * user and scanner tables put filter/search values into the *query string* of
 * the AJAX calls they make (e.g. `/api/exams/?patient_name=John&patient_msp=123`).
 * Sentry's fetch/xhr breadcrumbs capture those URLs by default, and any error or
 * transaction event then ships them to Sentry — so on a PHI/BAA app those query
 * values must be redacted before an event leaves the browser.
 *
 * We redact the *value* of every query parameter except a small allowlist of
 * operational params. This is fail-safe: a patient filter added later is scrubbed
 * automatically, and we never have to enumerate which keys are sensitive. We
 * deliberately do NOT touch path segments — legacy cloud paths contain long
 * snake_case route words and resource UUIDs that are not PHI and are useful for
 * debugging.
 */

// Query params whose values are safe to keep in Sentry breadcrumb/event URLs.
// Matched case-insensitively; everything else has its value redacted.
const SAFE_QUERY_PARAMS = new Set(["page", "page_size", "page_num", "cursor", "offset", "limit", "ordering", "format"]);

const REDACTED = "[Filtered]";

/** Redact the value of every non-allowlisted query param in a URL/path string. */
export function redactUrl(value) {
    if (typeof value !== "string" || value === "") {
        return value;
    }
    try {
        const url = new URL(value, window.location.origin);
        let changed = false;
        for (const key of [...url.searchParams.keys()]) {
            if (!SAFE_QUERY_PARAMS.has(key.toLowerCase())) {
                url.searchParams.set(key, REDACTED);
                changed = true;
            }
        }
        if (!changed) {
            return value;
        }
        // Preserve whether the original was absolute or a bare path.
        const rebuilt = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? url.toString() : url.pathname + url.search + url.hash;
        // URLSearchParams percent-encodes the sentinel; restore it.
        return rebuilt.split(encodeURIComponent(REDACTED)).join(REDACTED);
    } catch (_e) {
        // Not a parseable URL — nothing query-shaped to redact.
        return value;
    }
}

/**
 * Redact URLs embedded in free text, e.g. ky's "Request failed …: GET <url>".
 * URLs contain no whitespace, so per-token redaction leaves the words intact.
 */
export function redactMessage(message) {
    if (typeof message !== "string" || message === "") {
        return message;
    }
    return message.replace(/\S+/g, (token) => redactUrl(token));
}

// Recursion bound for `extra`; also stops cyclic objects. Deeper values pass through.
const MAX_EXTRA_DEPTH = 8;

function isPlainObject(value) {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function redactExtraValue(value, depth) {
    if (typeof value === "string") {
        return redactMessage(value);
    }
    if (depth >= MAX_EXTRA_DEPTH) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => redactExtraValue(item, depth + 1));
    }
    if (isPlainObject(value)) {
        const out = {};
        for (const [key, item] of Object.entries(value)) {
            out[key] = redactExtraValue(item, depth + 1);
        }
        return out;
    }
    return value;
}

/**
 * Scrub string values in an event's `extra` — a bag no other scrub reaches.
 * Not applied to `event.contexts`: the credential pass would corrupt its
 * Sentry-managed trace/span ids.
 */
export function redactExtra(extra) {
    return redactExtraValue(extra, 0);
}

/**
 * httpContextIntegration copies document.referrer (the previous page URL) into
 * the `Referer` header. Matched case-insensitively — header casings vary.
 */
function redactRefererHeader(headers) {
    for (const [name, value] of Object.entries(headers)) {
        if (name.toLowerCase() === "referer" && typeof value === "string") {
            headers[name] = redactUrl(value);
        }
    }
}

/** Redact the request URL ky embeds in each exception value, in place. */
function redactExceptionValues(values) {
    for (const value of values) {
        if (typeof value?.value === "string") {
            value.value = redactMessage(value.value);
        }
    }
}

/**
 * Scrub query values out of a breadcrumb's URL fields. Mutates and returns the
 * breadcrumb so it can be used directly as Sentry's `beforeBreadcrumb` hook.
 */
export function scrubBreadcrumb(breadcrumb) {
    if (breadcrumb && typeof breadcrumb.message === "string") {
        breadcrumb.message = redactMessage(breadcrumb.message);
    }
    const data = breadcrumb?.data;
    if (data) {
        // navigation breadcrumbs use `to`/`from`; fetch & xhr use `url`.
        for (const key of ["url", "to", "from"]) {
            if (typeof data[key] === "string") {
                data[key] = redactUrl(data[key]);
            }
        }
    }
    return breadcrumb;
}

/**
 * Scrub credential/PHI-bearing fields of an event. Works for both error events
 * (`beforeSend`) and transactions (`beforeSendTransaction`).
 */
export function scrubEvent(event) {
    if (!event) {
        return event;
    }
    if (event.request && typeof event.request.url === "string") {
        event.request.url = redactUrl(event.request.url);
    }
    if (event.request?.headers) {
        redactRefererHeader(event.request.headers);
    }
    // browserTracing falls back to the raw pathname when it can't parameterize the route.
    if (typeof event.transaction === "string") {
        event.transaction = redactUrl(event.transaction);
    }
    if (typeof event.message === "string") {
        event.message = redactMessage(event.message);
    }
    if (event.exception && Array.isArray(event.exception.values)) {
        redactExceptionValues(event.exception.values);
    }
    if (event.extra) {
        event.extra = redactExtra(event.extra);
    }
    if (Array.isArray(event.breadcrumbs)) {
        for (const breadcrumb of event.breadcrumbs) {
            scrubBreadcrumb(breadcrumb);
        }
    }
    return event;
}
